import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { Col, FormCheck, FormControl, FormGroup, FormLabel, Row, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InfoCircle } from 'react-bootstrap-icons';
import CollapsibleSection from './CollapsibleSection';
import lodash from 'lodash';
import CreatableDropdown from '../components/common/CreatableDropdown';
import SearchableSelect from '../components/common/SearchableSelect';
import ControlledTableInput from './ControlledTableInput';

const getInitialCollapsed = form => {
  return form?.rows?.filter(row => row.defaultCollapsed).map(row => row.rowName) || [];
};

// Form Generator Component
const FormGenerator = forwardRef(
  ({ form, formValues = {}, onFormValuesChange, onSubmit, formDisabled = false }, ref) => {
    const [collapsedSections, setCollapsedSections] = useState(getInitialCollapsed(form));
    const [errors, setErrors] = useState({});

    // Helper function to get field key
    const getFieldKey = field => field.key || field.id;

    // Validation function
    const validateForm = values => {
      const newErrors = {};

      if (form?.rows) {
        for (const row of form.rows) {
          if (row.columns) {
            for (const column of row.columns) {
              const field = column.field;
              const fieldKey = getFieldKey(field);
              const value = values[fieldKey];

              if (field.required && (value === undefined || value === null || value === '')) {
                newErrors[fieldKey] = 'This field is required';
              }

              // Add custom validation if provided
              if (field.validation && typeof field.validation === 'function') {
                const validationResult = field.validation(value, values);
                if (validationResult && validationResult !== true) {
                  newErrors[fieldKey] = validationResult;
                }
              }
            }
          }
        }
      }

      setErrors(newErrors);
      return newErrors;
    };

    // Handle field value changes
    const handleFieldChange = (fieldKey, value) => {
      const newValues = { ...formValues, [fieldKey]: value };
      onFormValuesChange(newValues);

      // Clear error for this field when user starts typing
      if (errors[fieldKey]) {
        const newErrors = { ...errors };
        delete newErrors[fieldKey];
        setErrors(newErrors);
      }
    };

    // Scroll to first error field
    const scrollToFirstError = errorFields => {
      // Find the first error field in the order they appear in the form
      let firstErrorField = null;

      if (form?.rows) {
        for (const row of form.rows) {
          if (row.columns) {
            for (const column of row.columns) {
              const field = column.field;
              const fieldKey = getFieldKey(field);
              if (errorFields[fieldKey]) {
                firstErrorField = fieldKey;
                break;
              }
            }
            if (firstErrorField) break;
          }
        }
      }

      if (firstErrorField) {
        const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }
    };

    // Handle form submission
    const submitForm = async () => {
      const validationErrors = validateForm(formValues);

      if (Object.keys(validationErrors).length === 0) {
        onSubmit && onSubmit(lodash.cloneDeep(formValues));
        return true; // Success
      } else {
        // Scroll to first error after a brief delay to ensure errors are rendered
        setTimeout(() => {
          scrollToFirstError(validationErrors);
        }, 100);
        return false; // Validation failed
      }
    };

    // Expose submitForm function through ref
    useImperativeHandle(ref, () => ({
      submitForm
    }));

    // Toggle section collapse
    const toggleSection = sectionKey => {
      setCollapsedSections(prev =>
        prev.includes(sectionKey) ? prev.filter(key => key !== sectionKey) : [...prev, sectionKey]
      );
    };
    // Styles for dropdowns
    const inventoryDropdownStyles = {
      menuPortal: base => ({
        ...base,
        zIndex: 11000
      }),
      control: (base, state) => ({
        ...base,
        borderRadius: '1.2rem',
        minHeight: '31px',
        fontSize: '0.875rem',
        backgroundColor: '#fff',
        borderColor: state.isFocused ? 'rgb(253.1455223881, 8.3544776119, 97.3694029851)' : '#ced4da',
        boxShadow: state.isFocused ? '0 0 0 0.15rem rgba(253, 8, 97, 0.25)' : 'none',
        '&:hover': {
          borderColor: '#ced4da'
        }
      }),
      valueContainer: base => ({
        ...base,
        padding: '0 10px'
      }),
      indicatorsContainer: base => ({
        ...base,
        paddingRight: '8px'
      }),
      dropdownIndicator: base => ({
        ...base,
        padding: 0,
        color: '#6c757d'
      }),
      indicatorSeparator: () => ({ display: 'none' }),
      clearIndicator: () => ({ display: 'none' }),
      singleValue: base => ({
        ...base,
        fontSize: '0.875rem'
      }),
      placeholder: base => ({
        ...base,
        fontSize: '0.875rem',
        color: '#6c757d'
      }),
      input: base => ({
        ...base,
        fontSize: '0.875rem',
        margin: 0,
        padding: 0
      }),
      menu: base => ({
        ...base,
        fontSize: '14px'
      }),
      option: base => ({
        ...base,
        fontSize: '14px',
        padding: '6px 8px'
      })
    };

    // Render form rows
    const renderFormRows = form => {
      return form.rows?.map((row, rowIndex) => {
        const { collapsible, width = '100%', className = '', rowName, title, showTitle = true } = row;
        const isCollapsed = collapsedSections.includes(rowName);
        const rowTitle = title || rowName;

        const renderRowContent = () => (
          <>
            {rowTitle && showTitle && (
              <>
                <h6 className="mt-2 mb-0">
                  <b>{rowTitle}</b>
                </h6>
                <hr class="mb-1" />
              </>
            )}
            <Row className={`mb-1 ${className}`}>
              {row.columns?.map((column, colIndex) => {
                const field = column.field;
                const fieldKey = getFieldKey(field);
                const CustomRender = field.render;

                return (
                  <Col
                    key={colIndex}
                    xs={column.xs || column.default || 12}
                    md={column.md || column.default || 6}
                    lg={column.lg || column.default || 6}
                    className={`${column.alignStart ? 'align-self-start' : 'align-self-end'} ${column.className ||
                      'my-1'}`}
                  >
                    <FormGroup className="mb-0">
                      {(field.title || field.titleKey) && (
                        <FormLabel>
                          <h6 className="mb-0 midFont">
                            {field.titleKey || field.title}
                            {field.required && <span className="text-danger">*</span>}
                            {field.infoText && (
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id={`info-${field.id || field.key || field.title}`}>
                                    {field.infoText}
                                  </Tooltip>
                                }
                              >
                                <span
                                  className="ms-1 text-primary"
                                  style={{ display: 'inline-flex', alignItems: 'center' }}
                                >
                                  <InfoCircle size={14} />
                                </span>
                              </OverlayTrigger>
                            )}
                          </h6>
                        </FormLabel>
                      )}

                      {/* Text Input */}
                      {field.type === 'text' && (
                        <FormControl
                          size="sm"
                          type="text"
                          name={fieldKey}
                          value={formValues[fieldKey] || ''}
                          onChange={e => {
                            handleFieldChange(fieldKey, e.target.value);
                            field.onChange && field.onChange(e.target.value);
                          }}
                          disabled={field.disabled || formDisabled}
                          placeholder={field.placeholder}
                        />
                      )}

                      {/* Textarea */}
                      {field.type === 'textarea' && (
                        <FormControl
                          size="sm"
                          as="textarea"
                          name={fieldKey}
                          value={formValues[fieldKey] || ''}
                          onChange={e => {
                            handleFieldChange(fieldKey, e.target.value);
                            field.onChange && field.onChange(e.target.value);
                          }}
                          disabled={field.disabled || formDisabled}
                          placeholder={field.placeholder}
                          rows={field.rows || 3}
                        />
                      )}

                      {/* Number Input */}
                      {field.type === 'number' && (
                        <FormControl
                          size="sm"
                          type="number"
                          name={fieldKey}
                          value={formValues[fieldKey] || ''}
                          onChange={e => {
                            handleFieldChange(fieldKey, e.target.value);
                            field.onChange && field.onChange(e.target.value);
                          }}
                          disabled={field.disabled || formDisabled}
                          placeholder={field.placeholder}
                          min={field.min}
                          max={field.max}
                          step={field.step}
                        />
                      )}

                      {/* Email Input */}
                      {field.type === 'email' && (
                        <FormControl
                          size="sm"
                          type="email"
                          name={fieldKey}
                          value={formValues[fieldKey] || ''}
                          onChange={e => {
                            handleFieldChange(fieldKey, e.target.value);
                            field.onChange && field.onChange(e.target.value);
                          }}
                          disabled={field.disabled || formDisabled}
                          placeholder={field.placeholder}
                        />
                      )}

                      {/* Password Input */}
                      {field.type === 'password' && (
                        <FormControl
                          size="sm"
                          type="password"
                          name={fieldKey}
                          value={formValues[fieldKey] || ''}
                          onChange={e => {
                            handleFieldChange(fieldKey, e.target.value);
                            field.onChange && field.onChange(e.target.value);
                          }}
                          disabled={field.disabled || formDisabled}
                          placeholder={field.placeholder}
                        />
                      )}

                      {/* Date Input */}
                      {field.type === 'date' && (
                        <FormControl
                          size="sm"
                          type="date"
                          name={fieldKey}
                          value={formValues[fieldKey] || ''}
                          onChange={e => {
                            handleFieldChange(fieldKey, e.target.value);
                            field.onChange && field.onChange(e.target.value);
                          }}
                          disabled={field.disabled || formDisabled}
                          min={field.min}
                          max={field.max}
                        />
                      )}

                      {/* Select Dropdown */}
                      {(field.type === 'select' || field.type === 'dropdown') && (
                        <FormControl
                          as="select"
                          size="sm"
                          name={fieldKey}
                          value={formValues[fieldKey] || ''}
                          onChange={e => {
                            handleFieldChange(fieldKey, e.target.value);
                            field.onChange && field.onChange(e.target.value);
                            field.onChangeFunction && field.onChangeFunction(e);
                          }}
                          disabled={field.disabled || formDisabled}
                        >
                          {!field.required && <option value="">{field.placeholder || 'Select an option'}</option>}
                          {field.options?.map((option, index) => {
                            const label = option;
                            const value = field.optionValues?.[index] || option;

                            return (
                              <option key={value || index} value={value}>
                                {label}
                              </option>
                            );
                          })}
                        </FormControl>
                      )}

                      {/* Searchable Dropdown */}
                      {field.type === 'searchable-dropdown' && (
                        <SearchableSelect
                          options={(field.options || []).map((option, index) => ({
                            label: option,
                            value: field.optionValues?.[index] ?? option
                          }))}
                          value={formValues[fieldKey] || ''}
                          onChange={value => {
                            handleFieldChange(fieldKey, value);
                            field.onChange && field.onChange(value);
                          }}
                          isDisabled={field.disabled || formDisabled}
                          isClearable={!field.required}
                          placeholder={field.placeholder || 'Select...'}
                          size="sm"
                          className="inventory-creatable-dropdown"
                          styles={inventoryDropdownStyles}
                        />
                      )}

                      {/* Creatable Dropdown */}
                      {field.type === 'creatable-dropdown' && (
                        <CreatableDropdown
                          options={field.options || []}
                          optionValues={field.optionValues}
                          value={formValues[fieldKey] || ''}
                          onChange={value => {
                            handleFieldChange(fieldKey, value);
                            field.onChange && field.onChange(value);
                          }}
                          disabled={field.disabled || formDisabled}
                          required={field.required}
                          placeholder={field.placeholder || 'Select or create...'}
                          onCreateOptionClick={field.onCreateOptionClick}
                          styles={inventoryDropdownStyles}
                        />
                      )}

                      {/* Checkbox */}
                      {field.type === 'checkbox' && (
                        <div>
                          <FormCheck
                            type="checkbox"
                            name={fieldKey}
                            checked={formValues[fieldKey] || false}
                            onChange={e => {
                              handleFieldChange(fieldKey, e.target.checked);
                              field.onChange && field.onChange(e.target.checked);
                            }}
                            disabled={field.disabled || formDisabled}
                            label={field.label}
                          />
                        </div>
                      )}

                      {/* Radio Group */}
                      {field.type === 'radio' && field.options && (
                        <div>
                          {field.options.map((option, index) => {
                            const isObject = typeof option === 'object';
                            const label = isObject ? option.label : option;
                            const value = isObject ? option.value : option;

                            return (
                              <FormCheck
                                key={value || index}
                                type="radio"
                                name={fieldKey}
                                value={value}
                                checked={formValues[fieldKey] === value}
                                onChange={e => {
                                  handleFieldChange(fieldKey, e.target.value);
                                  field.onChange && field.onChange(e.target.value);
                                }}
                                disabled={field.disabled || formDisabled}
                                label={label}
                              />
                            );
                          })}
                        </div>
                      )}

                      {/* Table Input */}
                      {field.type === 'table-input' && (
                        <ControlledTableInput
                          id={field.id || fieldKey}
                          tableFields={field.tableFields || []}
                          value={formValues[fieldKey] || []}
                          onChange={nextValue => handleFieldChange(fieldKey, nextValue)}
                          disabled={field.disabled || formDisabled}
                          dropdownOptions={field.dropdownOptions || []}
                          defaultHideOptions={field.defaultHideOptions}
                          showHideToggle={field.showHideToggle}
                          showResetToDefault={field.showResetToDefault}
                          onResetToDefault={field.onResetToDefault}
                          size={field.size}
                        />
                      )}

                      {/* Custom Render */}
                      {field.type === 'custom' && CustomRender && CustomRender()}

                      {/* Error Display */}
                      {errors[fieldKey] && <div className="text-danger small mt-1">{errors[fieldKey]}</div>}
                    </FormGroup>
                  </Col>
                );
              })}
            </Row>
          </>
        );

        return (
          <div key={rowName || rowIndex} style={{ flex: `0 0 ${width}` }}>
            {collapsible ? (
              <CollapsibleSection
                sectionName={rowName}
                isCollapsed={isCollapsed}
                onShowHideClick={() => toggleSection(rowName)}
              >
                {renderRowContent()}
              </CollapsibleSection>
            ) : (
              renderRowContent()
            )}
          </div>
        );
      });
    };

    return <div style={{ display: 'flex', flexWrap: 'wrap' }}>{renderFormRows(form)}</div>;
  }
);

export default FormGenerator;
